import { Component, Input, OnInit, ChangeDetectorRef, OnDestroy, OnChanges } from '@angular/core';
import { buttonStatus } from '../../../../shared/constants/button-class';
import { Popover } from 'primeng/popover';
import { toggleEllipsisPopover } from 'src/app/modules/crm/shared/utilities/ellipsis-popover.util';
import {
  ellipsisOptions,
  ellipsisHeaderOptions,
} from 'src/app/modules/crm/shared/constants/ellipsis-options';
import { KeyMap } from '../../../../shared/interface/key-map.interface';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmationService } from 'primeng/api';
import { ElectroluxTableService } from './electrolux-table.service';
import { ServiceOrderService } from '../../service-order.service';
import { ServiceOrderDataService } from '../../service-order-data.service';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import moment from 'moment';
import {
  copy,
  getKeyByValue,
  getValueByKey,
  isEmpty,
} from 'src/app/modules/crm/shared/utilities/common-utilities';
import {
  getMMDDYYYFormat,
} from 'src/app/modules/crm/shared/utilities/date-utilities';
import { AzureLoginService } from 'src/app/services/azure-login.service';
import {
  onTableColumnChange,
  resolveSelectedColumns,
} from 'src/app/modules/crm/shared/utilities/crm-table-utilities';

@Component({
  standalone: false,
  selector: 'electrolux-table',
  templateUrl: 'electrolux-table.template.html',
  styleUrls: ['./electrolux-table.scss'],
  providers: [MessageService, ConfirmationService],
})
export class ElectroluxTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() displayDialog: boolean;
  dataList: any[];
  cols: any[];
  btnStatus: any = buttonStatus;
  selectedItem: any;
  ellipsisOptions: any[] = ellipsisOptions;
  ellipsisHeaderOptions: any[] = copy(ellipsisHeaderOptions);
  selectedEllipsisItem: any;
  clonedRowData: KeyMap = {};
  isEditing: boolean;
  editConstant: string;
  selectedRowIndex: number;
  selectedColumns: any[];
  private readonly tableColumnId = 'electrolux-table';
  selectedServicer: any;
  isLoading = false;
  daysOptions: any[];
  selectedNoDays = '14';
  private isDataLoaded = false;
  customerDetails = {
    zipCode: 'N/A',
    address1: 'N/A',
    city: 'N/A',
    state: 'N/A',
    customerPhone: 'N/A',
    customerLastName: 'N/A',
  };
  querySubscriptions: Subscription[] = [];
  appointmentSearchSubscription: Subscription;
  
  constructor(
    private elxTableService: ElectroluxTableService,
    private confirmationService: ConfirmationService,
    private serviceOrderService: ServiceOrderService,
    private serviceOrderDataService: ServiceOrderDataService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private azureService: AzureLoginService,
  ) {}

  ngOnInit() {
    this.editConstant = uuidv4();
    this.cols = [
      { field: 'select', header: 'SELECT', type: 'radio' },
      { field: 'serviceProviderCenterId', header: 'SERVICE CENTER ID', type: 'text' },
      { field: 'recall', header: 'RECALL', type: 'checkbox' },
      { field: 'formattedAvailableDate', header: 'AVAILABLE DATE', type: 'text' },
      { field: 'weekDay', header: 'WEEKDAY', type: 'text' },
      { field: 'formattedTimePeriod', header: 'TIME PERIOD', type: 'text' },
    ];
    if (this.azureService.roleName === 'Admin') {
      this.cols = [
        ...this.cols,
        { field: 'serviceProviderName', header: 'SERVICE PROVIDER NAME', type: 'text' },
      ];
    }
    this.selectedColumns = resolveSelectedColumns(
      this.tableColumnId,
      this.cols,
      this.cols.slice(0, 8),
    );
    this.daysOptions = [
      { label: '7', value: '7' },
      { label: '14', value: '14' },
      { label: '21', value: '21' },
      { label: '28', value: '28' },
    ];
    this.selectedNoDays = '14';
    // Data loading happens in ngOnChanges when displayDialog is true
    // ngOnChanges is called by Angular before ngOnInit when inputs are set
  }

  onColumnChange(): void {
    this.selectedColumns = onTableColumnChange(
      this.tableColumnId,
      this.cols,
      this.selectedColumns,
    );
  }

  ngOnChanges() {
    // Load data only once when dialog opens
    if (this.displayDialog && !this.isDataLoaded) {
      this.isDataLoaded = true;
      this.isLoading = true;
      this.loadAppointmentData();
    }
    // Reset flag when dialog closes (so data reloads next time)
    if (!this.displayDialog) {
      this.isDataLoaded = false;
    }
  }

  loadAppointmentData() {
    const { claimData } = this.serviceOrderDataService.serviceOrderDataModel;
    if (claimData && claimData.serviceOrderNumber) {
      this.loadCustomerDetails();
      this.getAppointmentAvailability(claimData.serviceOrderNumber);
    } else {
      this.isLoading = false;
    }
  }

  onDaysChange(event: any) {
    this.dataList = [];
    this.selectedNoDays = event?.value || '14';
    this.loadAppointmentData();
  }

  loadCustomerDetails() {
    const locationDetails = this.serviceOrderDataService.serviceOrderDataModel.location;
    const customerInfo = this.serviceOrderDataService.serviceOrderDataModel.customerInfo;
    
    if (locationDetails) {
      this.customerDetails.zipCode = locationDetails.zipCode || 'N/A';
      this.customerDetails.address1 = locationDetails.address1 || 'N/A';
      this.customerDetails.city = locationDetails.city || 'N/A';
      this.customerDetails.state = locationDetails.state?.value || 'N/A';
    }
    
    if (customerInfo) {
      this.customerDetails.customerPhone = customerInfo.customerPhone || 'N/A';
      this.customerDetails.customerLastName = customerInfo.customerLastName || 'N/A';
    }
  }

  getAppointmentAvailability(serviceOrderNumber: string) {
    this.isLoading = true;
    this.appointmentSearchSubscription = this.elxTableService
      .getAppointmentSearch(serviceOrderNumber, this.selectedNoDays)
      .subscribe(
        (data: any) => {
          this.isLoading = false;
          if (this.appointmentSearchSubscription) {
            this.appointmentSearchSubscription.unsubscribe();
          }
          
          // Check for success status and appointmentSlots
          const isSuccess = data && (data.status === 'success' || data.returnType === 'Success');
          const slots = data?.appointmentSlots || data?.electroluxResponse?.appointmentSlots;
          
          if (isSuccess && slots && slots.length > 0) {
            const finalTableValues = [];
            const slotsArray = Array.isArray(slots) ? slots : [slots];
            
            for (const slot of slotsArray) {
              const scheduledDate = slot.scheduleDate || slot.ScheduleDate;
              const timePeriod = slot.timePeriod || slot.TimePeriod;
              const offerToken = slot.offerToken || slot.OfferToken;
              const groupNumber = slot.groupNumber || slot.GroupNumber || '';
              
              const formattedDate = this.parseElectroluxDate(scheduledDate);
              finalTableValues.push({
                serviceProviderCenterId: groupNumber || 'N/A',
                serviceProviderName: 'Electrolux',
                recall: false,
                availableDate: scheduledDate,
                formattedAvailableDate: formattedDate,
                timePeriod: timePeriod,
                formattedTimePeriod: this.formatTimePeriod(timePeriod),
                weekDay: formattedDate ? moment(formattedDate, 'MM/DD/YYYY').format('dddd') : 'N/A',
                // Electrolux specific fields for booking
                scheduledDate: scheduledDate,
                offerToken: offerToken,
                groupNumber: groupNumber,
              });
            }
            
            finalTableValues.sort(this.compareSlots);
            this.dataList = finalTableValues;
          } else {
            const errorMsg = data && data.message ? data.message : 'No appointment slots available';
            this.messageService.add({
              severity: 'warn',
              summary: 'Warning',
              detail: errorMsg,
            });
            this.dataList = [];
          }
        },
        (err) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error fetching appointment slots. Please try again.',
          });
          throw err;
        },
      );
  }

  formatTimePeriod(timePeriod: string): string {
    if (!timePeriod) return 'N/A';
    // Format time period like "8-12" to "8:00 AM - 12:00 PM"
    const parts = timePeriod.split('-');
    if (parts.length === 2) {
      const start = parseInt(parts[0], 10);
      const end = parseInt(parts[1], 10);
      const startStr = start < 12 ? `${start}:00 AM` : `${start === 12 ? 12 : start - 12}:00 PM`;
      const endStr = end < 12 ? `${end}:00 AM` : `${end === 12 ? 12 : end - 12}:00 PM`;
      return `${startStr} - ${endStr}`;
    }
    return timePeriod;
  }

  parseElectroluxDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    
    // Handle YYYYMMDD format (e.g., "20260311")
    if (dateStr.length === 8 && !dateStr.includes('-') && !dateStr.includes('/')) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${month}/${day}/${year}`;
    }
    
    // Handle ISO format (e.g., "2026-03-11" or "2026-03-11T00:00:00")
    if (dateStr.includes('-')) {
      const datePart = dateStr.split('T')[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
    }
    
    // Try moment as fallback
    const parsed = moment(dateStr);
    if (parsed.isValid()) {
      return parsed.format('MM/DD/YYYY');
    }
    
    return dateStr;
  }

  compareSlots(a: any, b: any): number {
    const dateA = a.availableDate;
    const dateB = b.availableDate;
    const timeA = a.timePeriod;
    const timeB = b.timePeriod;

    if (dateA === dateB) {
      if (timeA > timeB) return 1;
      if (timeA < timeB) return -1;
      return 0;
    }
    if (dateA > dateB) return 1;
    if (dateA < dateB) return -1;
    return 0;
  }

  ellipsisClick(event: any, item: any, Popover: Popover, rowIndex: number) {
    this.selectedItem = item;
    this.selectedRowIndex = rowIndex;
    toggleEllipsisPopover(event, Popover);
  }

  ellipsisOptionClick(event: any) {
    // ellipsis functionality goes here
  }

  onRowEdit() {
    this.isEditing = true;
    document.getElementById(this.editConstant + this.selectedItem.id)?.click();
  }

  onRowEditInit(rowData: any) {
    this.clonedRowData[rowData.id] = { ...rowData };
  }

  onRowEditSave(rowData: any) {
    delete this.clonedRowData[rowData.id];
    this.isEditing = false;
  }

  onRowEditCancel(rowData: any, index: number) {
    if (rowData[`isNew`]) {
      this.confirmationService.confirm({
        message: 'Are you sure that you want to discard, these changes?',
        accept: () => {
          this.dataList[index] = this.clonedRowData[rowData.id];
          delete this.clonedRowData[rowData.id];
          this.isEditing = false;
          this.dataList.splice(this.selectedRowIndex, 1);
        },
      });
    } else {
      this.dataList[index] = this.clonedRowData[rowData.id];
      delete this.clonedRowData[rowData.id];
      this.isEditing = false;
    }
  }

  onRadioButtonClick(event: any, rowData: any) {
    this.elxTableService.selectedValue = rowData;
  }

  ngOnDestroy() {
    this.querySubscriptions.forEach((subs) => subs.unsubscribe());
    if (this.appointmentSearchSubscription) {
      this.appointmentSearchSubscription.unsubscribe();
    }
  }
}
